import { useMemo } from 'react'
import { FormGroup } from '../types/CustomForm'
import { AnswerState } from '../components/DynamicForm'
import {
  TEAM_OFFICIALS_SECTION,
  PLAYER_INFO_SECTION,
  TEAM_OFFICIAL_FIELDS,
  PLAYER_INFO_FIELDS,
  matchesReservedField,
  ReservedField,
} from '../constants/prefillMappings'

interface SelectedPlayer {
  id?: string
  name_first?: string
  name_last?: string
  email?: string
  birth_date?: string
  [key: string]: any
}

interface PrefillResult {
  /** Pre-filled answers to merge with existing form answers */
  prefilledAnswers: AnswerState
  /** Set of field IDs that should be disabled */
  disabledFieldIds: Set<string>
  /**
   * Every reserved field this hook governs, whether or not it had a value to
   * prefill.
   *
   * `prefilledAnswers` alone cannot tell a caller what to CLEAR: switching from
   * a chosen person to "create new" yields an empty object, which is
   * indistinguishable from "nothing to do" — so the previous person's name would
   * stay in the form. This names the fields the selection owns, so the caller can
   * empty exactly those and leave the reader's own answers alone.
   */
  reservedFieldIds: Set<string>
}

// Helper to determine if a field is reserved (handles 1/0, true/false, etc.)
function isFieldReserved(is_reserved: any): boolean {
  // Accepts 1, true, '1', 'true' as reserved
  return (
    is_reserved === 1 ||
    is_reserved === true ||
    is_reserved === '1' ||
    is_reserved === 'true'
  )
}

/**
 * 🔄 usePrefillReservedFields: Hook to prefill and disable reserved form fields
 *
 * This hook analyzes the selected form and selectedPlayer data to automatically
 * populate reserved fields with existing player information and mark them as disabled.
 * Uses the new key-value answer format where answers are keyed by item.id.
 *
 * For player registrations, the Player Email field is always prefilled with the logged
 * user's email and disabled, regardless of whether a player is selected.
 *
 * @param selectedForm - The form definition (team_form or player_form)
 * @param selectedPlayer - The selected player/team official data (can be null for player registrations)
 * @param isTeamRep - Whether this is for team registration (vs player registration)
 * @param existingAnswers - Current form answers to avoid overwriting
 * @param loggedUserEmail - Email of the logged-in user (used for Player Email field)
 * @param loggedUserBirthDate - Birth date of the logged-in user (used for Player Birth Date field)
 * @returns Object containing prefilled answers and disabled field IDs
 */
export const usePrefillReservedFields = (
  selectedForm: { groups: FormGroup[] } | null,
  selectedPlayer: SelectedPlayer | null,
  isTeamRep: boolean,
  _existingAnswers: AnswerState = {},
  loggedUserEmail: string = '',
  loggedUserBirthDate: string | null = null
): PrefillResult => {
  return useMemo(() => {
    const prefilledAnswers: AnswerState = {}
    const disabledFieldIds = new Set<string>()
    const reservedFieldIds = new Set<string>()

    // Early return if no form definition
    if (!selectedForm?.groups) {
      return { prefilledAnswers, disabledFieldIds, reservedFieldIds }
    }

    // Use safe player object - null/undefined becomes empty object
    const safePlayer = selectedPlayer ?? ({} as SelectedPlayer)

    // Skip prefilling if selectedPlayer is explicitly set to 'new' (creating new player/official)
    const shouldSkipPrefilling = selectedPlayer?.id === 'new'

    // Determine which section and fields to look for based on registration type
    const targetSectionName = isTeamRep
      ? TEAM_OFFICIALS_SECTION
      : PLAYER_INFO_SECTION
    const fieldMappings = isTeamRep ? TEAM_OFFICIAL_FIELDS : PLAYER_INFO_FIELDS

    /**
     * Find the section holding the reserved identity fields.
     *
     * The name is preferred but NOT required. It is configured per competition
     * and really does vary — a team form in production names this section
     * "Information", not "Team Official", and the name-only lookup found
     * nothing and prefilled nothing.
     *
     * `is_reserved` is the API's own marker for that section, so it is the
     * reliable signal; the name is used only to disambiguate when a form
     * carries more than one reserved group. Falling back to the first reserved
     * group is safe because the field mappings still have to match by name
     * inside it — a reserved section without the expected fields simply
     * prefills nothing, exactly as before.
     */
    const reservedGroups = selectedForm.groups.filter((group) =>
      isFieldReserved(group.is_reserved)
    )
    const targetSection =
      reservedGroups.find((group) => group.name === targetSectionName) ??
      reservedGroups[0]

    if (!targetSection) {
      return { prefilledAnswers, disabledFieldIds, reservedFieldIds }
    }

    // Process each field mapping
    fieldMappings.forEach((mapping: ReservedField) => {
      // Find the corresponding form item
      const formItem = targetSection.items.find(
        (item) =>
          isFieldReserved(item.is_reserved) &&
          matchesReservedField(item.name, mapping.fieldName)
      )

      if (!formItem) {
        return
      }

      // Tracked regardless of whether a value is found — the caller needs the
      // full set to know what a selection change should clear.
      reservedFieldIds.add(formItem.id)

      // Get the value from selectedPlayer or use logged user data for specific fields
      let valueToUse: any
      if (mapping.playerKey === 'email' && isTeamRep) {
        // A team rep registers SOMEONE ELSE — the official they picked — so the
        // email belongs to that person, not to the account doing the
        // registering. No fallback to the logged-in user: an official with no
        // email on file leaves the field empty and EDITABLE (see the disable
        // rule below) so the reader can type the right one, rather than being
        // handed their own address to submit on someone else's behalf.
        //
        // Create-new is covered by `shouldSkipPrefilling`, which leaves this
        // undefined and so leaves the field blank.
        valueToUse = shouldSkipPrefilling ? undefined : safePlayer.email
      } else if (mapping.playerKey === 'email' && loggedUserEmail) {
        // Player registrations keep the logged user's email: the reader is
        // registering within their own family, so the account's address is the
        // contact address.
        valueToUse = loggedUserEmail
      } else if (
        mapping.playerKey === 'birth_date' &&
        isTeamRep &&
        (!safePlayer[mapping.playerKey] ||
          safePlayer[mapping.playerKey] === '') &&
        loggedUserBirthDate
      ) {
        // For team rep, use logged user birth date if selected player has no birth date
        valueToUse = loggedUserBirthDate
      } else if (!shouldSkipPrefilling) {
        // Only use player data if not explicitly creating new player/official
        valueToUse = safePlayer[mapping.playerKey]
      }

      // Special handling for email field - always disable it when logged user email is available
      if (mapping.playerKey === 'email' && loggedUserEmail && !isTeamRep) {
        // Player registrations only. The address is the reader's own account, so
        // it is not theirs to change here.
        //
        // A team rep is deliberately excluded: their email field is the chosen
        // official's, which may be missing or wrong on file. Disabling it on the
        // way past would leave them unable to correct it — and with nothing to
        // correct it TO, since there is no fallback value. When the official
        // does have one, the generic rule below still disables it.
        disabledFieldIds.add(formItem.id)
      }

      if (
        valueToUse !== undefined &&
        valueToUse !== null &&
        valueToUse !== ''
      ) {
        let processedValue = valueToUse

        // Special handling for birth_date fields - convert ISO date to YYYY-MM-DD format
        if (
          mapping.playerKey === 'birth_date' &&
          typeof valueToUse === 'string'
        ) {
          // Check if it's an ISO date string (contains 'T' for time)
          if (valueToUse.includes('T')) {
            // Extract just the date part (YYYY-MM-DD) from ISO string
            processedValue = valueToUse.split('T')[0]
          }
        }

        prefilledAnswers[formItem.id] = processedValue

        // Commented out because we want to disable all fields for now
        // Disable field if it has a prefilled value (except for email which is handled separately)
        // if (mapping.playerKey !== 'email') {
        disabledFieldIds.add(formItem.id)
        // }
      }
    })

    return { prefilledAnswers, disabledFieldIds, reservedFieldIds }
    // Dont add the existingAnswers to the deps array because it will cause a infinite loop
  }, [
    selectedForm,
    selectedPlayer,
    isTeamRep,
    loggedUserEmail,
    loggedUserBirthDate,
  ])
}
